import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ActionButton from './UI/ActionButton';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';


// Undo a repayment, or put back (redo) one that was undone: the one form
// everywhere (the Loans page, the Dashboard, the Repayments page).
//   undo — the loan's repayments, narrowed by a From / To date; the one the
//          user came from is chosen already.
//   redo — the repayments that were undone and not put back yet (of the loan,
//          or of every loan from the Repayments page), with why they were undone.
// A reason is mandatory, and "Are you sure?" is asked before it is sent; the
// API keeps the reason on the loan and in the audit trail.
// ctx: { loanId?, loanCode?, repaymentId? }; mode: 'undo' (default) or 'redo'.
const UndoRepaymentConfirm = ({ ctx = {}, mode = 'undo', onClose, onSuccess }) => {
  const redo = mode === 'redo';
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(!redo && ctx.repaymentId ? String(ctx.repaymentId) : null);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const keyOf = (p) => String(redo ? p.note_id : p.id);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { loan_id: ctx.loanId || undefined, from: from || undefined, to: to || undefined };
    axios.get(apiUrl(redo ? '/api/repayments/undone' : '/api/repayments'), {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      params: redo ? params : { ...params, page_size: 100, sort: 'newest' },
    })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res.data?.items) ? res.data.items : []); })
      .catch((e) => { if (!cancelled) { setItems([]); setError(e?.response?.data?.error || 'The repayments could not be loaded.'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ctx.loanId, from, to, redo]);

  const selected = items.find((p) => keyOf(p) === selectedKey) || null;
  const reasonOk = reason.trim().length >= 3;
  const verb = redo ? 'Redo' : 'Undo';

  // Submit asks first; "Yes" sends it.
  const askToConfirm = () => {
    if (!selected) { setError(`Choose the repayment to ${redo ? 'put back' : 'undo'}.`); return; }
    if (!reasonOk) { setError(`Give the reason the repayment is ${redo ? 'put back' : 'undone'}.`); return; }
    setError('');
    setConfirming(true);
  };

  const send = async () => {
    try {
      setSubmitting(true);
      setError('');
      const url = redo
        ? `/api/loans/${selected.loan_id}/undone-repayments/${selected.note_id}/redo`
        : `/api/loans/${ctx.loanId || selected.loan_id}/repayments/${selected.id}/undo`;
      await axios.post(apiUrl(url), { reason: reason.trim() }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (onSuccess) onSuccess();
    } catch (e) {
      const data = e?.response?.data;
      const baseMsg = data?.error || data?.message || e?.message || `Could not ${redo ? 'put back' : 'undo'} the repayment.`;
      setError(`${baseMsg}${data?.reference ? ` (Reference: ${data.reference})` : ''}`);
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'border border-gray-300 dark:border-slate-500 rounded py-2 px-3 w-full text-sm bg-gray-50 dark:bg-slate-600 dark:text-slate-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden w-full max-w-2xl max-h-[90vh] flex flex-col"
        style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(245,158,11,0.12)' }}
      >
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #f59e0b, #d97706)' }} />
        <div className="px-5 py-3.5 border-b border-amber-100/60 dark:border-slate-700 flex items-center justify-between"
          style={{ background: 'linear-gradient(to right, #f9fafb, #fffbeb55)' }}
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-gradient-to-b from-amber-400 to-amber-600 inline-block flex-shrink-0" />
            {verb} Repayment{ctx.loanCode ? ` — ${ctx.loanCode}` : ''}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 dark:text-slate-400 hover:bg-amber-100/60 dark:hover:bg-slate-700 hover:text-amber-700 dark:hover:text-slate-200 transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto text-sm">
          {/* Narrow the repayments by their payment date. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-medium mb-1">Date from</p>
              <input type="date" className={input} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <p className="font-medium mb-1">Date to</p>
              <input type="date" className={input} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <div>
            <p className="font-medium mb-1">{redo ? 'Undone repayment to put back' : 'Repayment to undo'} <span className="text-red-500">*</span></p>
            <div className="border border-gray-200 dark:border-slate-600 rounded max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700">
              {loading ? (
                <p className="p-3 text-gray-500 dark:text-slate-400">Loading repayments…</p>
              ) : items.length === 0 ? (
                <p className="p-3 text-gray-500 dark:text-slate-400">
                  {redo ? 'No undone repayments to put back' : 'No repayments'}{from || to ? ' in these dates' : ''}.
                </p>
              ) : items.map((p) => {
                const isOn = keyOf(p) === selectedKey;
                return (
                  <label key={keyOf(p)} className={`flex items-start gap-3 px-3 py-2 cursor-pointer ${isOn ? 'bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}>
                    <input type="radio" className="mt-1" name="repayment-choice" checked={isOn} onChange={() => { setSelectedKey(keyOf(p)); setError(''); }} />
                    <span className="w-24 text-gray-700 dark:text-slate-200">{p.payment_date ? formatDateDMY(p.payment_date) : '—'}</span>
                    <span className="w-28 font-semibold text-green-700 dark:text-green-400">{toCurrency(p.amount_paid)}</span>
                    <span className="flex-1 min-w-0 text-gray-600 dark:text-slate-300">
                      <span className="block truncate" title={`${p.payment_method || ''} ${p.payment_reference || ''}`}>
                        {!ctx.loanId && p.loan_code ? `${p.loan_code} · ` : ''}{p.payment_method || '—'}{p.payment_reference ? ` · ${p.payment_reference}` : ''}
                      </span>
                      {redo && p.undo_reason && (
                        <span className="block text-xs text-gray-500 dark:text-slate-400 truncate" title={p.undo_reason}>Undone: {p.undo_reason}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <p className="font-medium mb-1">Reason for {redo ? 'putting it back' : 'undoing'} <span className="text-red-500">*</span></p>
            <textarea
              className={`${input} h-20 resize-y ${reasonOk ? '' : '!border-red-400 dark:!border-red-500'}`}
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError(''); setConfirming(false); }}
              placeholder={redo ? 'Why is this repayment put back? (kept on the loan)' : 'Why is this repayment undone? (kept on the loan)'}
            />
          </div>

          {selected && (
            <p className="text-gray-700 dark:text-slate-200">
              The repayment of <span className="font-semibold">{toCurrency(selected.amount_paid)}</span>
              {selected.payment_date ? <> dated <span className="font-semibold">{formatDateDMY(selected.payment_date)}</span></> : null}
              {redo ? ' will be entered on the loan again.' : ' will be undone. This may change the loan status back to '}
              {!redo && <><span className="font-semibold">Outstanding</span> if there is any amount due.</>}
            </p>
          )}

          {error && (
            <div className="p-2.5 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400" role="alert">{error}</div>
          )}
        </div>

        {/* Submit asks first; the question replaces the buttons until answered. */}
        <div className="px-5 py-3 border-t border-amber-50 dark:border-slate-700">
          {confirming && selected ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-medium text-gray-800 dark:text-slate-100">
                Are you sure you want to {redo ? 'put back' : 'undo'} the repayment of {toCurrency(selected.amount_paid)}
                {selected.payment_date ? ` dated ${formatDateDMY(selected.payment_date)}` : ''}?
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium text-sm border border-gray-200 dark:border-slate-600"
                >
                  No
                </button>
                <ActionButton
                  type="button"
                  onClick={send}
                  disabled={submitting}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm shadow-sm disabled:opacity-50"
                >
                  {submitting ? (redo ? 'Putting back…' : 'Undoing…') : `Yes, ${verb.toLowerCase()} it`}
                </ActionButton>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium text-sm border border-gray-200 dark:border-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={askToConfirm}
                disabled={!selected || !reasonOk}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium text-sm shadow-sm disabled:opacity-50"
              >
                {verb} Repayment
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UndoRepaymentConfirm;
